"use client";

import { supabase } from "@/lib/supabaseClient";
import posthogClient, { type PostHog } from "posthog-js";

const SEED_KEY = "chicky-guest-voice-seed-v1";
const RESET_PENDING_KEY = "chicky-guest-voice-reset-pending-v1";
const ENDPOINT = "/api/voice-runtime/guest";
let preparation: Promise<boolean> | null = null;

export function guestVoiceAnalyticsAllowed(
  posthog?: Pick<PostHog, "get_distinct_id" | "has_opted_out_capturing"> | null
): boolean {
  return Boolean(
    posthog &&
    typeof posthog.has_opted_out_capturing === "function" &&
    !posthog.has_opted_out_capturing() &&
    /^[0-9a-f-]{36}$/i.test(posthog.get_distinct_id())
  );
}

export function guestVoiceAnalyticsHeaders(): Record<string, string> {
  return { "x-guest-voice-analytics": guestVoiceAnalyticsAllowed(posthogClient) ? "1" : "0" };
}

async function request(action: string, body: Record<string, unknown> = {}, accessToken?: string) {
  return fetch(ENDPOINT, {
    method: "POST",
    credentials: "same-origin",
    signal: AbortSignal.timeout(4000),
    headers: {
      "Content-Type": "application/json",
      ...guestVoiceAnalyticsHeaders(),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ action, ...body }),
  });
}

// Call only under the same Web Lock as prepare/reset. Keep the durable marker
// until both server cookie deletion and local cleanup have succeeded.
async function finishGuestVoiceReset(): Promise<boolean> {
  try {
    const response = await request("reset");
    if (!response.ok) return false;
    localStorage.removeItem(SEED_KEY);
    localStorage.removeItem(RESET_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}

export async function prepareGuestVoiceStart(
  posthog: PostHog | null | undefined
): Promise<boolean> {
  if (
    process.env.NEXT_PUBLIC_GUEST_VOICE_RUNTIME_ENABLED !== "true" ||
    typeof window === "undefined" ||
    window.top !== window.self ||
    !["/", "/landing", "/onboarding"].includes(
      window.location.pathname.replace(/\/$/, "") || "/"
    ) ||
    !guestVoiceAnalyticsAllowed(posthog)
  )
    return false;
  if (preparation) return preparation;
  const prepare = async () => {
    try {
      if (localStorage.getItem(RESET_PENDING_KEY) === "1") {
        if (!(await finishGuestVoiceReset())) return false;
      }
      let seed = localStorage.getItem(SEED_KEY);
      if (!seed) {
        seed = crypto.randomUUID();
        localStorage.setItem(SEED_KEY, seed);
      }
      const response = await request("prepare", {
        seed,
        analyticsId: posthog!.get_distinct_id(),
        consent: true,
      });
      return response.ok && (await response.json()).ready === true;
    } catch {
      return false;
    }
  };
  // No unsafe cross-tab fallback: browsers lacking Web Locks stay outside enrollment.
  if (!navigator.locks) return false;
  preparation = navigator.locks.request(SEED_KEY, prepare);
  try {
    return await preparation;
  } finally {
    preparation = null;
  }
}

export async function stageGuestVoiceSignup(): Promise<void> {
  if (process.env.NEXT_PUBLIC_GUEST_VOICE_RUNTIME_ENABLED !== "true") return;
  try {
    await request("signup_intent");
  } catch {
    /* Routing still reads the durable assignment. */
  }
}

export async function bindGuestVoiceSignup(): Promise<void> {
  if (process.env.NEXT_PUBLIC_GUEST_VOICE_RUNTIME_ENABLED !== "true") return;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  const response = await request("bind", {}, session.access_token);
  if (!response.ok) throw new Error("Guest voice assignment binding unavailable");
}

export function registerGuestVoiceAnalytics(
  posthog: PostHog | null | undefined,
  value: unknown
): void {
  if (!guestVoiceAnalyticsAllowed(posthog)) return;
  if (!value || typeof value !== "object") {
    // Keep original assignment attribution, but don't mislabel a subsequent
    // unsupported/forced session with the preceding room's guest/auth scope.
    posthog!.unregister("voice_runtime_session_user_type");
    return;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record.assignmentId !== "string" ||
    !/^[0-9a-f-]{36}$/i.test(record.assignmentId) ||
    typeof record.experimentKey !== "string" ||
    !/^[a-z0-9_]{1,120}$/.test(record.experimentKey) ||
    !["main3_render", "half_aws"].includes(String(record.variant))
  )
    return;
  posthog!.register({
    guest_voice_runtime_assignment_id: record.assignmentId,
    guest_voice_runtime_experiment_key: record.experimentKey,
    guest_voice_runtime_variant: record.variant,
    voice_runtime_session_user_type: record.sessionUserType === "guest" ? "guest" : "authenticated",
    [`$feature/${record.experimentKey}`]: record.variant,
  });
}

export async function resetGuestVoiceIdentity(posthog?: PostHog): Promise<void> {
  if (typeof window === "undefined") return;
  if (posthog) {
    const key = posthog.get_property("guest_voice_runtime_experiment_key");
    for (const property of [
      "guest_voice_runtime_assignment_id",
      "guest_voice_runtime_experiment_key",
      "guest_voice_runtime_variant",
      "voice_runtime_session_user_type",
    ])
      posthog.unregister(property);
    if (typeof key === "string") posthog.unregister(`$feature/${key}`);
  }
  const reset = async () => {
    try {
      localStorage.setItem(RESET_PENDING_KEY, "1");
      localStorage.removeItem(SEED_KEY);
    } catch {
      /* Guest preparation also stays disabled while storage is blocked. */
    }
    if (process.env.NEXT_PUBLIC_GUEST_VOICE_RUNTIME_ENABLED !== "true") return;
    // A failed attempt is retried by the next preparation, including after reload.
    await finishGuestVoiceReset();
  };
  // Serialize cookie deletion with preparation across tabs.
  if (navigator.locks) await navigator.locks.request(SEED_KEY, reset);
  else await reset();
}
